// Multi-wheel manager — shows numbered chips (1)(2)... for each placed
// wheel and a "+" icon button (lucide Plus — the button itself is
// already circular, so a plain plus stroke, not CirclePlus) while
// placedCount < maxWheels.
// Clicking a chip selects that wheel (routes through the controller);
// clicking the plus button places a new wheel at the current center-
// screen hit. Positioned inside the manual-adjustment panel's upper-
// left corner (above the joystick).

import { Plus } from 'lucide-react'
import type { WheelId } from '@/ar/wheel-renderer'
import {
  SELECTION_RING_COLOR_HEX,
  SELECTION_RING_SEGMENTS,
  SELECTION_RING_GAP_DEG,
} from '@/ar/selection-ring'

/** One green for selection, shared with the ring drawn on the wheel. */
const SELECTION_GREEN = SELECTION_RING_COLOR_HEX

/** Ring radius inside the chip, inset for the 2px stroke. */
const PENDING_RING_RADIUS = (size: number) => size / 2 - 1

/** The dark disc inside a ringed chip is drawn this much smaller across
 *  than the ring, so a sliver of camera shows between them and the ring
 *  reads as a highlight around the icon rather than its border. The
 *  stroke itself is unchanged. Tuned on device: 4px, 7px, 9px, then
 *  11px (2026-08-23). */
const RINGED_CHIP_FILL_INSET_PX = 11

/** Outline weight for the nav chips. A 1px hairline vanished against a
 *  bright scene — over grass the numbers were unreadable (2026-08-24
 *  LUAT). 3px reads at a glance on any background. */
export const CHIP_OUTLINE_PX = 3

/** The default, unselected outline. */
export const CHIP_OUTLINE_COLOR = 'rgba(255,255,255,0.92)'

/** Same value as the component's local DARK_BG. */
const DARK_BG_CONST = 'rgba(0, 0, 0, 0.85)'

/**
 * Every chip is exactly `size` across, whatever it is wearing.
 *
 * Nothing in this app sets box-sizing, so the default content-box was
 * in force and `width: size` + `border: 3px` rendered at size + 6 —
 * while the SELECTED chip (border: none) rendered at size. The row held
 * chips of two different diameters, and raising the outline from 1px to
 * 3px silently grew all of them (2026-08-24 LUAT: "the diameter should
 * never change ... so that the diameter of circle element is not
 * affected by style changes"). border-box makes the outline paint
 * inward, so state and stroke width are free to change without ever
 * touching the footprint.
 */
const CHIP_BOX: React.CSSProperties = { boxSizing: 'border-box' }

/** Gap between a chip and the green line under it. */
export const PENDING_LINE_GAP_PX = 3

/** Thickness of that line. */
const PENDING_LINE_PX = 2

function RingedChipFill({ size, round }: { size: number; round: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        // Centred on the chip's midpoint rather than measured in from
        // its edge. An absolutely positioned child resolves against the
        // PADDING box — inside the border — so a top/left inset landed
        // the disc a full outline-width down and to the right of centre
        // (2026-08-24 LUAT: "the dark disks are not even aligned within
        // the circles"). A midpoint translate cannot drift, whatever
        // the border does.
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: size - RINGED_CHIP_FILL_INSET_PX,
        height: size - RINGED_CHIP_FILL_INSET_PX,
        borderRadius: round ? 999 : 8,
        background: DARK_BG_CONST,
        pointerEvents: 'none',
      }}
    />
  )
}

/**
 * Dash pattern for a 6-segment ring with 10-degree gaps — the chip
 * equivalent of the dashed ring on the wheel being aimed.
 *
 * Exported so the arithmetic is testable: it is easy to write a
 * dasharray that happens to look plausible while producing seven
 * segments, or gaps that do not close the circle.
 */
export function pendingRingDashArray(size: number): string {
  const circumference = 2 * Math.PI * PENDING_RING_RADIUS(size)
  const period = circumference / SELECTION_RING_SEGMENTS
  const gap = circumference * (SELECTION_RING_GAP_DEG / 360)
  return `${(period - gap).toFixed(3)} ${gap.toFixed(3)}`
}

interface WheelSelectorProps {
  /** A placement is armed and its preview is on screen. The user has
   *  already pressed "+", so show that wheel's chip instead of another
   *  "+" — offering it again invites pressing it twice (2026-08-23). */
  pendingChip?: boolean
  /** Round chips — the touch-mode look, where these are the primary
   *  controls. The arrow-pad layout keeps its original squared chips. */
  round?: boolean
  /** ?touchUi — outline the selected chip in the same green as the ring
   *  on the wheel itself, so the chip and the scene name the same
   *  selection rather than each having a private idea of it. */
  selectedOutline?: boolean
  wheelIds: readonly WheelId[]
  selectedId: WheelId | null
  maxWheels: number
  onSelect: (id: WheelId) => void
  onAdd: () => void
  /** Chip / plus-button diameter in px. Default 28 (panel-inset chips);
   *  v2 floating row overrides to 44 to match the header icons. */
  size?: number
  /** When true, don't absolutely position — render as a normal flex
   *  row (parent controls placement). Used by v2's floating row. */
  inline?: boolean
  /** Rotate chip glyphs 90° for landscape reading. Container size and
   *  layout unchanged. */
  isLandscape?: boolean
  /** Stack chips top-to-bottom instead of left-to-right. Used by the
   *  landscape wheels-nav strip that sits to the LEFT of the B
   *  container so its chips run along the physical chassis short
   *  edge. */
  vertical?: boolean
  /** With `vertical`, use column-reverse so the first chip lands at
   *  the BOTTOM — the strip reads bottom-to-top like the portrait row
   *  reads left-to-right (chassis-riding order for angle 90). */
  reversed?: boolean
}

export function WheelSelector({
  wheelIds,
  selectedId,
  maxWheels,
  onSelect,
  onAdd,
  size = 23,
  selectedOutline = false,
  pendingChip = false,
  round = false,
  inline = false,
  isLandscape = false,
  vertical = false,
  reversed = false,
}: WheelSelectorProps) {
  void isLandscape
  const canAdd = wheelIds.length < maxWheels
  const columnDir = reversed ? 'column-reverse' : 'column'
  // --el-rot cascades from the parent container (set by App): in-place
  // glyph rotation during pinned-DOM AR sessions, 0deg otherwise.
  const glyphStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(var(--el-rot, 0deg))',
  }
  const wrapperStyle: React.CSSProperties = inline
    ? {
        display: 'flex',
        flexDirection: vertical ? columnDir : 'row',
        gap: 8,
        alignItems: 'center',
      }
    : {
        position: 'absolute',
        top: 8,
        left: 12,
        display: 'flex',
        flexDirection: vertical ? columnDir : 'row',
        gap: 6,
        alignItems: 'center',
      }
  const iconSize = Math.round(size * 0.55)
  return (
    <div data-testid="wheel-selector" style={wrapperStyle}>
      {wheelIds.map((id, i) => {
        const isSelected = id === selectedId
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            data-testid={`wheel-chip-${i + 1}`}
            aria-label={`Select wheel ${i + 1}`}
            aria-pressed={isSelected}
            style={{
              ...CHIP_BOX,
              position: 'relative',
              width: size,
              height: size,
              // Round under touchUi: a circle tells the user these
              // belong to the wheel, not to a generic toolbar.
              borderRadius: round ? 999 : 8,
              // The selected chip wears the SAME dashed ring as the
              // wheel it points at (drawn as SVG below) — a solid border
              // said something different from the scene (2026-08-23).
              // Selected swaps the solid outline for the green dashed
              // ring drawn below; every other state keeps the 3px
              // outline. The dark disc inside is permanent either way.
              border: isSelected
                ? 'none'
                : `${CHIP_OUTLINE_PX}px solid ${CHIP_OUTLINE_COLOR}`,
              // A ringed chip paints its dark disc as an inset child so
              // it can sit inside the ring; everything else keeps its
              // own background.
              // The disc is drawn as an inset child in every state, so
              // the button itself stays clear.
              background: 'transparent',
              boxShadow: inline ? '0 2px 4px rgba(0,0,0,0.2)' : undefined,
              color: isSelected ? SELECTION_GREEN : 'rgba(255,255,255,0.92)',
              fontSize: Math.round(size * 0.42),
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            {/* Dashed selection ring — same six segments and gaps as
                the ring on the wheel, so chip and scene agree. Not drawn
                while a placement is armed: that wheel is the one being
                aimed, and two green rings in the row is ambiguous. */}
            <RingedChipFill size={size} round={round} />
            {isSelected && selectedOutline && !pendingChip && (
              <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ position: 'absolute', inset: 0 }}
                aria-hidden
              >
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={PENDING_RING_RADIUS(size)}
                  fill="none"
                  stroke={SELECTION_GREEN}
                  strokeWidth={CHIP_OUTLINE_PX}
                  strokeDasharray={pendingRingDashArray(size)}
                  strokeLinecap="butt"
                />
              </svg>
            )}
            <span style={glyphStyle}>{i + 1}</span>
          </button>
        )
      })}
      {pendingChip && (
        <span
          data-testid="wheel-chip-pending"
          aria-label={`Placing wheel ${wheelIds.length + 1}`}
          style={{
            // A bare wrapper the exact size of the chip. The green line
            // hangs off it absolutely, so the line's weight, gap and
            // length are all free to change without ever resizing the
            // circle or nudging the row (2026-08-24 LUAT).
            ...CHIP_BOX,
            position: 'relative',
            display: 'inline-flex',
            flex: '0 0 auto',
            width: size,
            height: size,
          }}
        >
          <span
            data-testid="wheel-chip-pending-circle"
            style={{
              ...CHIP_BOX,
              position: 'relative',
              width: size,
              height: size,
              // Reads as a NORMAL, unselected chip: the same white
              // outline and dark disc as every other. The only mark is
              // the green line below, mirroring the gauge line the
              // scene draws under the wheel being aimed. Selection is
              // the dashed ring, and this is not selection.
              background: 'transparent',
              border: `${CHIP_OUTLINE_PX}px solid ${CHIP_OUTLINE_COLOR}`,
              borderRadius: round ? 999 : 8,
              color: 'rgba(255,255,255,0.92)',
              fontSize: Math.round(size * 0.42),
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            <RingedChipFill size={size} round={round} />
            <span style={glyphStyle}>{wheelIds.length + 1}</span>
          </span>
          {/* The chip mirrors what the SCENE is showing. While aiming,
              the scene draws a straight green line under the wheel — so
              the chip draws one under itself, spanning the full
              diameter. */}
          <span
            data-testid="wheel-chip-pending-line"
            aria-hidden
            style={{
              position: 'absolute',
              top: '100%',
              marginTop: PENDING_LINE_GAP_PX,
              left: 0,
              width: size,
              height: PENDING_LINE_PX,
              borderRadius: PENDING_LINE_PX / 2,
              background: SELECTION_GREEN,
            }}
          />
        </span>
      )}
      {canAdd && !pendingChip && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="wheel-add-button"
          aria-label="Add another wheel"
          style={{
            ...CHIP_BOX,
            position: 'relative',
            width: size,
            height: size,
            borderRadius: round ? 999 : 8,
            // Same treatment as the wheel chips — a hairline outline
            // disappeared over grass (2026-08-24 LUAT).
            border: `${CHIP_OUTLINE_PX}px solid ${CHIP_OUTLINE_COLOR}`,
            background: 'transparent',
            boxShadow: inline ? '0 2px 4px rgba(0,0,0,0.2)' : undefined,
            color: 'rgba(255,255,255,0.92)',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          {/* Outside the glyph span on purpose: that span carries a
              rotate transform, and a transform establishes a containing
              block, so a disc nested inside it positioned against the
              ROTATED box instead of the button — visibly off-centre on
              the "+" (2026-08-24 LUAT). */}
          <RingedChipFill size={size} round={round} />
          <span style={{ ...glyphStyle, position: 'relative' }}>
            <Plus size={iconSize} strokeWidth={2.25} />
          </span>
        </button>
      )}
    </div>
  )
}
